import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  SimpleGrid,
  Stat,
  StatLabel,
  StatNumber,
  StatHelpText,
  StatArrow,
  Heading,
  Text,
  VStack,
  HStack,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Badge,
  Progress,
  useColorModeValue,
  Icon,
  Flex,
  Button,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  Spinner,
  Alert,
  AlertIcon,
  useToast,
  Grid,
  GridItem,
  Tabs,
  TabList,
  TabPanels,
  Tab,
  TabPanel,
} from '@chakra-ui/react';
import {
  FaChartLine,
  FaUsers,
  FaRupeeSign,
  FaCheckCircle,
  FaClock,
  FaChevronDown,
  FaDownload,
  FaFilter,
} from 'react-icons/fa';
import { Line, Doughnut } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  ArcElement,
} from 'chart.js';
import api from '../utils/api';
import { useAuth } from '../hooks/useAuth';
import AgentIDCard from '../components/AgentIDCard';
import SubAgentsList from '../components/SubAgentsList';
import CommissionHistory from '../components/CommissionHistory';
import config from '../config';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  ArcElement
);

const StatCard = ({ label, value, icon, change, changeType, isLoading }) => {
  const bgColor = useColorModeValue('white', 'gray.800');
  const textColor = useColorModeValue('gray.600', 'gray.200');
  const valueColor = useColorModeValue('gray.900', 'white');
  
  return (
    <Box
      p={{ base: 3, md: 6 }}
      bg={bgColor}
      borderRadius="lg"
      shadow="md"
      _hover={{ shadow: 'lg' }}
      transition="all 0.3s"
    >
      <HStack spacing={{ base: 2, md: 4 }}>
        <Icon as={icon} w={{ base: 6, md: 8 }} h={{ base: 6, md: 8 }} color="brand.500" />
        <Stat>
          <VStack align="start" spacing={1}>
            <StatLabel fontSize={{ base: "xs", md: "sm" }} color={textColor}>
              {label}
            </StatLabel>
            {isLoading ? (
              <Spinner size="sm" />
            ) : (
              <>
                <StatNumber fontSize={{ base: "lg", md: "2xl" }} fontWeight="bold" color={valueColor}>
                  {value}
                </StatNumber>
                {change && (
                  <StatHelpText fontSize={{ base: "xs", md: "sm" }} color={textColor}>
                    <StatArrow type={changeType} />
                    {change}
                  </StatHelpText>
                )}
              </>
            )}
          </VStack>
        </Stat>
      </HStack>
    </Box>
  );
};

const RecentTransactions = ({ transactions = [], isLoading }) => {
  const bgColor = useColorModeValue('white', 'gray.800');

  const LoadingState = () => (
    <Box
      bg={bgColor}
      borderRadius="lg"
      p={{ base: 3, md: 6 }}
      shadow="md"
      w="100%"
      h="100%"
      minH={{ base: "300px", md: "400px" }}
    >
      <VStack spacing={{ base: 2, md: 4 }} align="stretch">
        <Heading size={{ base: "sm", md: "md" }}>Recent Transactions</Heading>
        <Spinner />
      </VStack>
    </Box>
  );

  if (isLoading) {
    return <LoadingState />;
  }

  if (!transactions || transactions.length === 0) {
    return (
      <Box
        bg={bgColor}
        borderRadius="lg"
        p={{ base: 3, md: 6 }}
        shadow="md"
        w="100%"
        h="100%"
        minH={{ base: "300px", md: "400px" }}
      >
        <VStack spacing={{ base: 2, md: 4 }} align="stretch">
          <Heading size={{ base: "sm", md: "md" }}>Recent Transactions</Heading>
          <Text>No recent transactions found</Text>
        </VStack>
      </Box>
    );
  }

  return (
    <Box
      bg={bgColor}
      borderRadius="lg"
      p={{ base: 3, md: 6 }}
      shadow="md"
      w="100%"
      minH={{ base: "300px", md: "400px" }}
    >
      <VStack spacing={{ base: 2, md: 4 }} align="stretch">
        <Heading size={{ base: "sm", md: "md" }}>Recent Transactions</Heading>
        <Box overflowX="auto">
          <Table variant="simple" size={{ base: "sm", md: "md" }}>
            <Thead>
              <Tr>
                <Th fontSize={{ base: "xs", md: "sm" }}>Date</Th>
                <Th fontSize={{ base: "xs", md: "sm" }}>Customer</Th>
                <Th fontSize={{ base: "xs", md: "sm" }}>Type</Th>
                <Th fontSize={{ base: "xs", md: "sm" }}>Amount</Th>
                <Th fontSize={{ base: "xs", md: "sm" }}>Status</Th>
              </Tr>
            </Thead>
            <Tbody>
              {transactions.map((tx) => (
                <Tr key={tx.id}>
                  <Td fontSize={{ base: "xs", md: "sm" }} whiteSpace="nowrap">
                    {new Date(tx.date).toLocaleDateString()}
                  </Td>
                  <Td fontSize={{ base: "xs", md: "sm" }} maxW="200px" isTruncated>{tx.customerName || 'N/A'}</Td>
                  <Td fontSize={{ base: "xs", md: "sm" }}>{tx.type || 'N/A'}</Td>
                  <Td fontSize={{ base: "xs", md: "sm" }} whiteSpace="nowrap">₹{tx.amount?.toLocaleString('en-IN') || '0'}</Td>
                  <Td fontSize={{ base: "xs", md: "sm" }}>
                    <Badge
                      colorScheme={
                        tx.status === 'approved'
                          ? 'green'
                          : tx.status === 'rejected'
                          ? 'red'
                          : 'yellow'
                      }
                    >
                      {tx.status || 'pending'}
                    </Badge>
                  </Td>
                </Tr>
              ))}
            </Tbody>
          </Table>
        </Box>
      </VStack>
    </Box>
  );
};

const PerformanceChart = ({ aumGrowth = [], isLoading }) => {
  const bgColor = useColorModeValue('white', 'gray.800');

  const LoadingState = () => (
    <Box
      bg={bgColor}
      borderRadius="lg"
      p={{ base: 3, md: 6 }}
      shadow="md"
      w="100%"
      minH={{ base: "300px", md: "400px" }}
    >
      <VStack spacing={{ base: 2, md: 4 }}>
        <Heading size={{ base: "sm", md: "md" }}>AUM Growth</Heading>
        <Spinner />
      </VStack>
    </Box>
  );

  if (isLoading) {
    return <LoadingState />;
  }

  if (!aumGrowth || aumGrowth.length === 0) {
    return (
      <Box
        bg={bgColor}
        borderRadius="lg"
        p={{ base: 3, md: 6 }}
        shadow="md"
        w="100%"
        minH={{ base: "300px", md: "400px" }}
      >
        <VStack spacing={{ base: 2, md: 4 }}>
          <Heading size={{ base: "sm", md: "md" }}>AUM Growth</Heading>
          <Text>No AUM data available</Text>
        </VStack>
      </Box>
    );
  }

  // Format the data for the chart
  const chartData = aumGrowth.map(item => ({
    month: item.month || `${new Date().getMonth() + 1}/${new Date().getFullYear()}`,
    amount: item.amount || 0
  }));

  const data = {
    labels: chartData.map(item => item.month),
    datasets: [
      {
        label: 'AUM Growth',
        data: chartData.map(item => item.amount),
        fill: false,
        borderColor: 'rgb(75, 192, 192)',
        tension: 0.1,
      },
    ],
  };

  const options = {
    responsive: true,
    plugins: {
      legend: {
        position: 'top',
      },
      title: {
        display: false,
      },
    },
    scales: {
      y: {
        beginAtZero: true,
        ticks: {
          callback: value => `₹${value.toLocaleString('en-IN')}`
        }
      }
    },
    tooltips: {
      callbacks: {
        label: (tooltipItem) => `₹${tooltipItem.value.toLocaleString('en-IN')}`
      }
    }
  };

  return (
    <Box
      bg={bgColor}
      borderRadius="lg"
      p={{ base: 3, md: 6 }}
      shadow="md"
      w="100%"
      minH={{ base: "300px", md: "400px" }}
    >
      <VStack spacing={{ base: 2, md: 4 }}>
        <Heading size={{ base: "sm", md: "md" }}>AUM Growth</Heading>
        <Box w="100%" h={{ base: "250px", md: "300px" }}>
          <Line options={options} data={data} />
        </Box>
      </VStack>
    </Box>
  );
};

const PortfolioDistribution = ({ distribution = [], isLoading }) => {
  const bgColor = useColorModeValue('white', 'gray.800');

  const LoadingState = () => (
    <Box
      bg={bgColor}
      borderRadius="lg"
      p={{ base: 3, md: 6 }}
      shadow="md"
      w="100%"
      minH={{ base: "300px", md: "400px" }}
    >
      <VStack spacing={{ base: 2, md: 4 }}>
        <Heading size={{ base: "sm", md: "md" }}>Portfolio Distribution</Heading>
        <Spinner />
      </VStack>
    </Box>
  );

  if (isLoading) {
    return <LoadingState />;
  }

  const data = {
    labels: distribution.map((item) => item._id),
    datasets: [
      {
        data: distribution.map((item) => item.total),
        backgroundColor: [
          '#FF6384',
          '#36A2EB',
          '#FFCE56',
          '#4BC0C0',
          '#9966FF',
        ],
      },
    ],
  };

  const options = {
    responsive: true,
    plugins: {
      legend: {
        position: 'right',
      },
    },
  };

  return (
    <Box
      bg={bgColor}
      borderRadius="lg"
      p={{ base: 3, md: 6 }}
      shadow="md"
      w="100%"
      minH={{ base: "300px", md: "400px" }}
    >
      <VStack spacing={{ base: 2, md: 4 }}>
        <Heading size={{ base: "sm", md: "md" }}>Portfolio Distribution</Heading>
        <Box w="100%" h={{ base: "250px", md: "300px" }}>
          <Doughnut data={data} options={options} />
        </Box>
      </VStack>
    </Box>
  );
};

const GoalProgress = ({ goals = [], isLoading }) => {
  const bgColor = useColorModeValue('white', 'gray.800');

  const LoadingState = () => (
    <Box
      bg={bgColor}
      borderRadius="lg"
      p={{ base: 3, md: 6 }}
      shadow="md"
      w="100%"
      minH={{ base: "300px", md: "400px" }}
    >
      <VStack spacing={{ base: 2, md: 4 }}>
        <Heading size={{ base: "sm", md: "md" }}>Goal Progress</Heading>
        <Spinner />
      </VStack>
    </Box>
  );

  if (isLoading) {
    return <LoadingState />;
  }

  return (
    <Box
      bg={bgColor}
      borderRadius="lg"
      p={{ base: 3, md: 6 }}
      shadow="md"
      w="100%"
      minH={{ base: "300px", md: "400px" }}
    >
      <VStack spacing={{ base: 2, md: 4 }}>
        <Heading size={{ base: "sm", md: "md" }}>Goal Progress</Heading>
        {goals.map((goal, index) => (
          <Box key={index} w="100%" mb={2}>
            <HStack justify="space-between" mb={2}>
              <Text fontSize={{ base: "xs", md: "sm" }}>{goal.title}</Text>
              <Text fontSize={{ base: "xs", md: "sm" }}>
                ₹{goal.current.toLocaleString('en-IN')} / ₹
                {goal.target.toLocaleString('en-IN')}
              </Text>
            </HStack>
            <Progress
              value={goal.progress}
              colorScheme={goal.progress >= 100 ? 'green' : 'blue'}
              borderRadius="full"
            />
          </Box>
        ))}
      </VStack>
    </Box>
  );
};

const AgentInfoCard = ({ user }) => {
  const bgColor = useColorModeValue('white', 'gray.800');
  const headingColor = useColorModeValue('gray.800', 'white');
  const textColor = useColorModeValue('gray.700', 'gray.200');

  return (
    <Box 
      mb={{ base: 4, md: 6 }} 
      p={{ base: 3, md: 4 }} 
      bg={bgColor}
      rounded="lg" 
      shadow="base"
    >
      <Heading size={{ base: "sm", md: "md" }} mb={4} color={headingColor}>
        Agent Information
      </Heading>
      <VStack align="stretch" spacing={3}>
        <Text fontSize={{ base: "sm", md: "md" }} color={textColor}>
          <strong>Agent ID:</strong> {user.agentId}
        </Text>
        <Text fontSize={{ base: "sm", md: "md" }} color={textColor}>
          <strong>Name:</strong> {user.name}
        </Text>
        <Text fontSize={{ base: "sm", md: "md" }} color={textColor}>
          <strong>Email:</strong> {user.email}
        </Text>
        <Text fontSize={{ base: "sm", md: "md" }} color={textColor}>
          <strong>Phone:</strong> {user.phone}
        </Text>
        <Text fontSize={{ base: "sm", md: "md" }} color={textColor}>
          <strong>Role:</strong> {user.role}
        </Text>
        <HStack>
          <Text fontSize={{ base: "sm", md: "md" }} color={textColor}>
            <strong>Status:</strong>
          </Text>
          <Badge colorScheme={user.isApproved ? 'green' : 'yellow'}>
            {user.isApproved ? 'Approved' : 'Pending Approval'}
          </Badge>
        </HStack>
      </VStack>
    </Box>
  );
};

const AgentDashboard = () => {
  const [dashboardData, setDashboardData] = useState({
    stats: {
      totalAUM: 0,
      activeClients: 0,
      monthlyCommission: 0,
      sipBook: 0
    },
    aumGrowth: [],
    portfolioDistribution: [],
    recentTransactions: [],
    goalProgress: []
  });
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const { user } = useAuth();
  const toast = useToast();
  const bgColor = useColorModeValue('gray.50', 'gray.900');

  useEffect(() => {
    const fetchDashboardData = async () => {
      try {
        setIsLoading(true);
        const response = await api.get('/api/agents/dashboard');

        if (response.data.success && response.data.data) {
          setDashboardData(response.data.data);
        } else {
          throw new Error('Invalid data structure received');
        }
        
        setError(null);
      } catch (err) {
        setError(err.response?.data?.message || 'Failed to fetch dashboard data');
        toast({
          title: 'Error',
          description: err.response?.data?.message || 'Failed to fetch dashboard data',
          status: 'error',
          duration: 5000,
          isClosable: true,
        });
      } finally {
        setIsLoading(false);
      }
    };

    if (user) {
      fetchDashboardData();
    }
  }, [user, toast]);

  if (!user) {
    return (
      <Container maxW="container.xl" py={8}>
        <Alert status="warning">
          <AlertIcon />
          Please log in to view your dashboard
        </Alert>
      </Container>
    );
  }

  if (error) {
    return (
      <Container maxW="container.xl" py={8}>
        <Alert status="error">
          <AlertIcon />
          {error}
        </Alert>
      </Container>
    );
  }

  return (
    <Box minH="calc(100vh - 64px)" bg={bgColor} py={{ base: 4, md: 8 }}>
      <Container maxW="container.xl" px={{ base: 2, md: 4, lg: 6 }}>
        <Grid
          templateColumns={{ 
            base: 'repeat(1, 1fr)',
            lg: 'repeat(3, 1fr)' 
          }}
          gap={{ base: 4, md: 6 }}
        >
          <GridItem colSpan={1}>
            <AgentInfoCard user={user} />
          </GridItem>
          <GridItem colSpan={{ base: 1, lg: 2 }}>
            <SimpleGrid 
              columns={{ base: 1, sm: 2, xl: 4 }} 
              spacing={{ base: 3, md: 6 }}
            >
              <StatCard
                label="Total AUM"
                value={`₹${dashboardData.stats.totalAUM.toLocaleString('en-IN')}`}
                icon={FaChartLine}
                isLoading={isLoading}
              />
              <StatCard
                label="Active Clients"
                value={dashboardData.stats.activeClients}
                icon={FaUsers}
                isLoading={isLoading}
              />
              <StatCard
                label="Monthly Commission"
                value={`₹${dashboardData.stats.monthlyCommission.toLocaleString('en-IN')}`}
                icon={FaRupeeSign}
                isLoading={isLoading}
              />
              <StatCard
                label="SIP Book"
                value={`₹${dashboardData.stats.sipBook.toLocaleString('en-IN')}`}
                icon={FaCheckCircle}
                isLoading={isLoading}
              />
            </SimpleGrid>
          </GridItem>
        </Grid>

        <Box mt={{ base: 4, md: 8 }}>
          <Tabs colorScheme="brand" isLazy>
            <TabList overflowX="auto" overflowY="hidden" whiteSpace="nowrap">
              <Tab fontSize={{ base: "sm", md: "md" }}>Dashboard Overview</Tab>
              {user.role === 'agent' && <Tab fontSize={{ base: "sm", md: "md" }}>Sub-Agents</Tab>}
              <Tab fontSize={{ base: "sm", md: "md" }}>Commission History</Tab>
            </TabList>

            <TabPanels>
              <TabPanel p={{ base: 2, md: 4 }}>
                <Grid 
                  templateColumns={{ 
                    base: '1fr',
                    lg: 'repeat(2, 1fr)' 
                  }} 
                  gap={{ base: 4, md: 8 }}
                >
                  <GridItem>
                    <VStack spacing={8} align="stretch">
                      <PerformanceChart
                        aumGrowth={dashboardData.aumGrowth}
                        isLoading={isLoading}
                      />
                      <PortfolioDistribution
                        distribution={dashboardData.portfolioDistribution}
                        isLoading={isLoading}
                      />
                    </VStack>
                  </GridItem>
                  <GridItem>
                    <VStack spacing={8} align="stretch">
                      <RecentTransactions
                        transactions={dashboardData.recentTransactions}
                        isLoading={isLoading}
                      />
                      <GoalProgress
                        goals={dashboardData.goalProgress}
                        isLoading={isLoading}
                      />
                    </VStack>
                  </GridItem>
                </Grid>
              </TabPanel>

              {user.role === 'agent' && (
                <TabPanel>
                  <Box>
                    <Heading size="md" mb={4}>Manage Sub-Agents</Heading>
                    <Text color="gray.600" mb={6}>
                      View and manage your sub-agents, their commission rates, and status.
                    </Text>
                    <SubAgentsList />
                  </Box>
                </TabPanel>
              )}

              <TabPanel>
                <Box>
                  <Heading size="md" mb={4}>Commission History</Heading>
                  <Text color="gray.600" mb={6}>
                    Track your commission earnings and distribution over time.
                  </Text>
                  <CommissionHistory />
                </Box>
              </TabPanel>
            </TabPanels>
          </Tabs>
        </Box>
      </Container>
    </Box>
  );
};

export default AgentDashboard;
